import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import { buildEnquiryMessage, WHATSAPP_NUMBER, WHATSAPP_DISPLAY } from "../../lib/whatsapp";

const FIELD_BASE =
  "w-full bg-transparent border-0 border-b border-charcoal/25 rounded-none px-0 py-4 text-charcoal placeholder-charcoal/40 focus:outline-none focus:border-forest transition-colors font-sans text-base";

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    quantity: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone so we can reach you.");
      return;
    }
    setSubmitting(true);
    const text = buildEnquiryMessage(form);
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;

    toast.success("Opening WhatsApp — send the pre-filled message to complete your enquiry.");

    // Slight delay so the toast is visible before window.open
    setTimeout(() => {
      window.open(url, "_blank", "noopener,noreferrer");
      setSubmitting(false);
    }, 550);
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-cream py-28 md:py-40 border-t border-charcoal/10"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-20">
          {/* Left column — headline + address */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              className="font-sans text-[11px] uppercase tracking-[0.32em] text-charcoal/60 mb-6 flex items-center gap-3"
            >
              <span className="text-gold">§</span> Enquire
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1, ease: [0.65, 0, 0.35, 1] }}
              className="font-serif font-light text-5xl md:text-6xl lg:text-[5rem] text-charcoal leading-[0.98]"
            >
              Tell us what <br />
              you <span className="italic text-forest">need.</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, delay: 0.15 }}
              className="mt-8 font-sans text-charcoal/70 text-base md:text-[17px] leading-relaxed max-w-[42ch]"
            >
              Fill this in and we'll continue the conversation on WhatsApp —
              faster than email, and you'll hear back the same day.
            </motion.p>

            <div className="mt-12 space-y-6 border-t border-charcoal/10 pt-8">
              <div className="flex items-start gap-4">
                <MapPin size={18} className="mt-1 text-gold" />
                <div>
                  <div className="font-sans text-[11px] uppercase tracking-[0.28em] text-charcoal/50 mb-1">
                    Address
                  </div>
                  <div className="font-serif text-xl text-charcoal">
                    Ongole, Prakasam District,
                    <br />
                    Andhra Pradesh, India
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone size={18} className="mt-1 text-gold" />
                <div>
                  <div className="font-sans text-[11px] uppercase tracking-[0.28em] text-charcoal/50 mb-1">
                    Direct line
                  </div>
                  <a
                    href={`tel:+${WHATSAPP_NUMBER}`}
                    data-testid="contact-phone-link"
                    className="font-serif text-xl text-charcoal hover:text-forest transition-colors"
                  >
                    {WHATSAPP_DISPLAY}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right column — form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="enquiry-form"
              className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2"
              noValidate
            >
              <div className="md:col-span-1">
                <label className="font-sans text-[11px] uppercase tracking-[0.28em] text-charcoal/50">
                  Name *
                </label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={set("name")}
                  data-testid="form-name-input"
                  placeholder="Your full name"
                  className={FIELD_BASE}
                />
              </div>
              <div className="md:col-span-1">
                <label className="font-sans text-[11px] uppercase tracking-[0.28em] text-charcoal/50">
                  Phone *
                </label>
                <input
                  type="tel"
                  required
                  value={form.phone}
                  onChange={set("phone")}
                  data-testid="form-phone-input"
                  placeholder="+countrycode 000-000-0000"
                  className={FIELD_BASE}
                />
              </div>
              <div className="md:col-span-1">
                <label className="font-sans text-[11px] uppercase tracking-[0.28em] text-charcoal/50">
                  Email <span className="text-charcoal/30">(optional)</span>
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  data-testid="form-email-input"
                  placeholder="you@company.com"
                  className={FIELD_BASE}
                />
              </div>
              <div className="md:col-span-1">
                <label className="font-sans text-[11px] uppercase tracking-[0.28em] text-charcoal/50">
                  Quantity Required
                </label>
                <input
                  type="text"
                  value={form.quantity}
                  onChange={set("quantity")}
                  data-testid="form-quantity-input"
                  placeholder="e.g. 500 kg / 1 MT / 20ft container"
                  className={FIELD_BASE}
                />
              </div>
              <div className="md:col-span-2">
                <label className="font-sans text-[11px] uppercase tracking-[0.28em] text-charcoal/50">
                  Message
                </label>
                <textarea
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  data-testid="form-message-input"
                  placeholder="Grade preference, destination port, timeline…"
                  className={`${FIELD_BASE} resize-none`}
                />
              </div>

              <div className="md:col-span-2 mt-10 flex flex-wrap items-center justify-between gap-6">
                <p className="font-sans text-charcoal/50 text-sm max-w-[46ch]">
                  On submit we'll open WhatsApp with a pre-filled message — you
                  just tap send.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="form-submit-button"
                  className="group inline-flex items-center gap-3 rounded-full bg-forest text-cream px-8 py-4 text-[12px] uppercase tracking-[0.28em] font-medium hover:bg-forestDeep transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? "Opening WhatsApp…" : "Send on WhatsApp"}
                  <ArrowUpRight
                    size={16}
                    className="transition-transform duration-500 group-hover:rotate-45"
                  />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
